/**
 * FRCHero — Weidner Via6 page hero for /future-residents-club.
 *
 * The Future Residents Club page is defined in this theme directly
 * (not inherited from rise), so it gets its own slot rather than one
 * of the slot names rise/requirements.ts reserves. Named `FRCHero` for
 * brevity to match the existing `frc-hero` section id in pages.json.
 *
 * See `FloorPlansHero.tsx` and `_PageImageHeroBody.tsx` for the shared
 * pattern. Owns its slot registration, contentSchema, and hidden
 * literal-path anchors; visual rendering is delegated to the body.
 */

import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { PageImageHeroBody } from './_PageImageHeroBody';

const bgDef = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Future Residents Club hero image',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

export function FRCHero() {
  return (
    <>
      <PageImageHeroBody
        titlePath="frchero.title"
        bgPath="frchero.bg_image"
        bgDef={bgDef}
        defaultTitle="Future Residents Club"
        keywords={['lifestyle', 'residents', 'lounge', 'lobby']}
        pageHint="frchero"
        altText="Future Residents Club hero image"
      />

      <RichTextContent
        path="frchero.title"
        as="span"
        className="hidden"
        style={{ display: 'none' }}
      />
      <EditableSlotGroup
        groupPath="frchero.bg_image"
        definition={bgDef}
        className="hidden"
        style={{ display: 'none' }}
      >
        <span />
      </EditableSlotGroup>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FRCHero',
  displayName: 'Future Residents Club Hero',
  tokens: {
    'frchero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Future Residents Club',
    },
  },
  slotGroups: {
    'frchero.bg_image': {
      label: 'Background Image',
      fields: {
        image: {
          label: 'Image',
          type: 'image',
          defaultField: 'property_featured_image',
        },
        alt: {
          label: 'Alt Text',
          type: 'text',
          defaultValue: 'Future Residents Club hero image',
        },
      },
      sourceBinding: {
        collection: 'photo_gallery',
        defaultIndex: 0,
        fieldMappings: { image: 'image', alt: 'alt_text' },
      },
    },
  },
};

(FRCHero as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default FRCHero;
